import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getSelectOptions, type SelectOptionsParams } from '@dc-trans/data-layer';

export interface SelectOption {
  label: string;
  value: string | number;
  [key: string]: any;
}

interface SelectOptionsState {
  // Store options by endpoint + params hash
  options: Record<string, SelectOption[]>;
  // Store labels mapping for quick lookup: endpoint -> { value: label }
  labels: Record<string, Record<string | number, string>>;
  // Loading states
  loading: Record<string, boolean>;
  // Actions
  setOptions: (_options: SelectOption[], _endpoint: string, _cacheKey?: string) => void;
  getOptions: (_key: string) => SelectOption[];
  getLabel: (_endpoint: string, _value: string | number) => string | undefined;
  fetchOptions: (
    _endpoint: string,
    _params?: SelectOptionsParams,
    _renderOption?: (_item: any) => SelectOption,
    _cacheKey?: string
  ) => Promise<void>;
  clearOptions: (_key: string) => void;
  reset: () => void;
}

// Helper to create cache key
const createCacheKey = (endpoint: string, cacheKey?: string): string => {
  return cacheKey ?? endpoint;
};

export const useSelectOptionsStore = create<SelectOptionsState>()(
  devtools(
    (set, get) => ({
      options: {},
      labels: {},
      loading: {},

      setOptions: (options: SelectOption[], endpoint: string, cacheKey?: string) => {
        const key = createCacheKey(endpoint, cacheKey);
        const labels = options.reduce(
          (acc, option) => {
            acc[option.value] = option.label;
            return acc;
          },
          {} as Record<string | number, string>
        );

        set(state => ({
          options: { ...state.options, [key]: options },
          labels: {
            ...state.labels,
            [key]: { ...state.labels[key], ...labels },
            [endpoint]: { ...state.labels[endpoint], ...labels },
          },
          loading: { ...state.loading, [key]: false },
        }));
      },

      getOptions: (key: string) => {
        return get().options[key] || [];
      },

      getLabel: (endpoint: string, value: string | number) => {
        const key = createCacheKey(endpoint);
        return get().labels[key]?.[value];
      },

      fetchOptions: async (endpoint, params, renderOption, cacheKey) => {
        const key = createCacheKey(endpoint, cacheKey);

        // Set loading state
        set(state => ({
          loading: { ...state.loading, [key]: true },
        }));

        try {
          const response = await getSelectOptions(endpoint, params);
          const options =
            response.data?.items?.map(
              renderOption ||
                ((item: any) => ({
                  label: item.name || item.label || item.title || item.ulid,
                  value: item.ulid || item.value,
                  ...item,
                }))
            ) || [];

          get().setOptions(options, endpoint, cacheKey);
        } catch (error) {
          console.error(`Error fetching options for ${endpoint}:`, error);
          set(state => ({
            loading: { ...state.loading, [key]: false },
          }));
        }
      },

      clearOptions: (key: string) => {
        set(state => {
          const newOptions = { ...state.options };
          const newLabels = { ...state.labels };
          const newLoading = { ...state.loading };

          delete newOptions[key];
          delete newLabels[key];
          delete newLoading[key];

          return {
            options: newOptions,
            labels: newLabels,
            loading: newLoading,
          };
        });
      },

      reset: () => {
        set({
          options: {},
          labels: {},
          loading: {},
        });
      },
    }),
    { name: 'select-options-store' }
  )
);
